import { AccountActions } from "@/components/common/section-components/action/AccountActions";
import { EstablishmentsFilter } from "@/components/common/section-components/filters/EstablishmentsFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { GetAccountListResponse, GetProductoresProps } from "@/services/accounts.service";
import { establishmentFilterSchema } from "./schema/filter-obj";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import {
  clearFiltersHandler,
  fetchEstablishmentsAndFieldsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { establishmentsAndFieldsHeaderElements } from "./schema/header-elements";

export const EstablishmentAndFieldsPage = () => {
  const navigate = useNavigate();

  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [establishments, setEstablishments] = useState<GetAccountListResponse | null>(null);
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<Partial<GetProductoresProps>>({});

  const { fetchPermissions } = useRoles();

  const { control, handleSubmit, setValue, getValues } = useForm({
    resolver: zodResolver(establishmentFilterSchema),
    defaultValues: {
      account_number: null,
      account_type: "",
      locality: "",
      commercial_zone: "",
      social_reason: "",
    },
  });

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchEstablishments = async (
    filters?: Partial<GetProductoresProps>,
    showLoad = true,
  ) => {
    await fetchEstablishmentsAndFieldsHandler({
      setLoading,
      setEstablishments,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setOpenFilters,
      setFiltersApplied,
      setCurrentFilters,
      setPage,
      fetchEstablishments,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      submitFilters,
    });
  };

  useEffect(() => {
    if (isActive) fetchEstablishments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]); // fetchEstablishments se redefine cada render; isActive es el trigger intencional de mount

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchEstablishments({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.ESTABLISHMENT_FIELD_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <AccountActions handleDetail={handleDetail} item={item} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px">
      <SectionHeader
        title="Maestro de establecimientos y lotes"
        showButtons={false}
        loading={loading}
      />

      <EstablishmentsFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loading}
        loadingFilters={loading}
        filtersApplied={filtersApplied}
        disableClear={loading}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !establishments?.listado?.length) && <NoData />}

        {Boolean(!loading && establishments?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              items={establishments?.listado ?? []}
              headers={establishmentsAndFieldsHeaderElements}
              actionElements={getActionElements}
              noSelection
            />

            {Boolean(establishments?.totalPages && establishments.totalPages > 1) && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={establishments?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
